import { makeAutoObservable } from 'mobx';
import { basedAxios } from '../../store/Store';
import { formatDateRange } from '../../utils/date.utils';

enum HotelValidationReasons {
  UnfinishedBooking = 'UnfinishedBooking',
  ReferencePersonRequired = 'ReferencePersonRequired',
}

export class HotelStore {
  rootStore: any = null;

  searchResultList: any = [];
  checkinDate: string | Date | number | null = null;
  checkoutDate: string | Date | number | null = null;
  roomCount: number = 1;
  roomSizes: number[] = [];
  fullbooked: boolean = false;
  fetching: boolean = false;
  fetched: boolean = false;
  fullbookedAllotments: any[] = [];
  hotels: any[] = [];
  hotelAllotments: any[] = [];
  selection: number[] = [];
  bookableRoomSizes: any = [];
  bookableDates: number[] = [];
  showNoResultMessage: boolean = false;
  eventId: number = 0;
  roomMates: any[] = [];

  settings: any = null;

  loaded: boolean = false;
  initialized: boolean = false;

  gapDates: { [key: number]: number } = {};

  requestedRoomSize: number | null = null;

  stay: boolean | null = null; // Do the person want to stay at a hotel
  book: boolean | null = null; // Do the person want to book a hotel room

  constructor(rootStore: any) {
    this.rootStore = rootStore;

    this.setInitialized = this.setInitialized.bind(this);
    this.setCheckinDate = this.setCheckinDate.bind(this);
    this.setCheckoutDate = this.setCheckoutDate.bind(this);
    this.setSelection = this.setSelection.bind(this);
    this.setStay = this.setStay.bind(this);
    this.setOrderExtra = this.setOrderExtra.bind(this);
    this.setBook = this.setBook.bind(this);
    this.configureDefaultDates = this.configureDefaultDates.bind(this);
    this.setRoomCount = this.setRoomCount.bind(this);
    this.setRoomSize = this.setRoomSize.bind(this);
    this.hideNoResultMessage = this.hideNoResultMessage.bind(this);
    this.validateReferencePerson = this.validateReferencePerson.bind(this);
    this.validateBooking = this.validateBooking.bind(this);
    this.fetchHotels = this.fetchHotels.bind(this);
    this.fetchAllotmentStatus = this.fetchAllotmentStatus.bind(this);
    this.fetchBookableRoomSizes = this.fetchBookableRoomSizes.bind(this);
    this.search = this.search.bind(this);
    this.fetchAvailableAllotmentsBySelection = this.fetchAvailableAllotmentsBySelection.bind(this);
    this.setRequestedRoomSize = this.setRequestedRoomSize.bind(this);
    this.removeOrderExtra = this.removeOrderExtra.bind(this);
    this.setSettings = this.setSettings.bind(this);

    this.fetchRoomMates = this.fetchRoomMates.bind(this);

    makeAutoObservable(this);
  }

  public setSettings(settings: any) {
    this.settings = settings;
  }

  public setRequestedRoomSize(value: number | null) {
    this.requestedRoomSize = value;
  }

  public setInitialized(valid: boolean = false) {
    this.initialized = true;
    this.validate(valid, HotelValidationReasons.UnfinishedBooking);
  }

  public setOrderExtra(name: string, value: string) {
    if (!this.rootStore.ordersLoaded) return;
    if (this.rootStore.orders.length && !this.rootStore.orders[0]?.orderExtra) {
      this.rootStore.orders[0].orderExtra = [];
    }
    
    const orderExtra = this.rootStore?.orders?.[0]?.orderExtra;
    const orderExtraIndex = orderExtra?.findIndex((oe: any) => oe.name === name);

    if (orderExtraIndex !== -1) {
      orderExtra[orderExtraIndex].value = value;
    } else {
      orderExtra?.push({ name, value });
    }

    this.validateReferencePerson();
  }

  public removeOrderExtra(name: string) {
    if (!this.rootStore.ordersLoaded) return;
    const orderExtra = this.rootStore?.orders?.[0]?.orderExtra;
    const orderExtraIndex = orderExtra?.findIndex((oe: any) => oe.name === name);

    if (orderExtraIndex !== -1) {
      orderExtra?.splice(orderExtraIndex, 1);
    }
  }

  private getOrderExtra(name: string) {
    if (!this.rootStore.ordersLoaded) return null;
    const orderExtra = this.rootStore?.orders?.[0]?.orderExtra;
    const orderExtraIndex = orderExtra?.findIndex((oe: any) => oe.name === name);

    if (orderExtraIndex !== -1 && orderExtra?.[orderExtraIndex]?.value) {
      return orderExtra[orderExtraIndex].value;
    } else {
      return null;
    }
  }

  public validateReferencePerson() {
    if (this.book === false) {
      const referencePerson = this.getOrderExtra('HotelReferencePerson');

      if (!referencePerson) {
        this.validate(false, HotelValidationReasons.ReferencePersonRequired);
        this.validate(false, HotelValidationReasons.UnfinishedBooking);
      } else {
        this.validate(true, HotelValidationReasons.UnfinishedBooking);
        this.validate(true, HotelValidationReasons.ReferencePersonRequired);
      }
    } else {
      this.validate(true, HotelValidationReasons.ReferencePersonRequired);
    }
  }

  public validateBooking = () => {
    const order = this.rootStore?.orders?.[0];
    const hasHotelRows = order?.orderRow?.some((or: any) => or?.hotelAllotmentPricingId && or.orderRowId < 0);

    if (hasHotelRows && this.stay && this.book) {
      this.validate(true, HotelValidationReasons.UnfinishedBooking);
    } else if (!hasHotelRows && this.stay && this.book) {
      this.validate(false, HotelValidationReasons.UnfinishedBooking);
    }
  };

  public setBook(value: boolean | null) {
    this.book = value;

    if (value === false) {
      this.validate(false, HotelValidationReasons.ReferencePersonRequired);
    }

    if (value === null) {
      this.validate(true, HotelValidationReasons.ReferencePersonRequired);
    }

    if (value === true) {
      this.validate(true, HotelValidationReasons.ReferencePersonRequired);
    }
  }

  public setStay(value: boolean | null) {
    this.stay = value;

    if (!value) {
      this.searchResultList = [];
      this.roomSizes = this.bookableRoomSizes.length > 0 ? [this.bookableRoomSizes[0]] : [];
      this.roomCount = 1;
      this.showNoResultMessage = false;
      this.fetched = false;
      this.setBook(null);
      this.validate(true, HotelValidationReasons.UnfinishedBooking);
      this.setOrderExtra('HotelStay', 'False');
    } else {
      this.validate(false, HotelValidationReasons.UnfinishedBooking);
      this.setOrderExtra('HotelStay', 'True');
    }
  }

  private validate(valid: boolean = false, reason: HotelValidationReasons | null = null) {
    const validations = this.rootStore.validations;

    if (validations.find((x: any) => x.label === 'HotelValidation' && x.reason === reason)) {
      validations.find((x: any) => x.label === 'HotelValidation' && x.reason === reason).valid = valid;
    } else {
      const contentLayoutModule = this.rootStore?.contentLayout?.contentLayoutModules.find(clm => clm?.contentModule?.type === 'HOTEL_MODULE');
      validations.push({
        label: 'HotelValidation',
        valid,
        reason,
        page: this.rootStore?.current?.activePage,
        id: contentLayoutModule?.contentModuleId,
        type: 'HotelValidation',
      });
    }
  }

  public hideNoResultMessage() {
    this.showNoResultMessage = false;
  }

  public setRoomCount(value: number) {
    this.roomCount = value;
  }

  public setRoomSize(value: number | Array<number>) {
    if (Array.isArray(value)) {
      this.roomSizes = value.filter(roomSize => typeof roomSize === 'number' && Number.isFinite(roomSize));
      return;
    }

    if (this.roomSizes.includes(value)) {
      this.roomSizes = this.roomSizes.filter(x => x !== value);
      return;
    }
    this.roomSizes = [...this.roomSizes, value];
  }

  public setCheckinDate(value: string | Date | number) {
    if (this.checkoutDate && +new Date(value) >= +new Date(this.checkoutDate)) {
      return;
    }

    this.checkinDate = value;
  }

  public setCheckoutDate(value: string | Date | number) {
    if (this.checkinDate && +new Date(value) <= +new Date(this.checkinDate)) {
      return;
    }
    this.checkoutDate = value;
  }

  public setSelection(selection: number[]) {
    this.selection = (selection ?? []).map(value => Number(value)).filter(value => Number.isFinite(value));
  }

  public configureDefaultDates(settings: any) {
    const selectionValues = settings?.selection ?? this.settings?.selection ?? [];
    const selectedRoomTypeIds = selectionValues.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value));

    const selectedAllotments = this.hotelAllotments.filter(({ hotelRoomType }: any) => {
      if (selectedRoomTypeIds.length === 0) return true;
      return selectedRoomTypeIds.includes(Number(hotelRoomType?.hotelRoomTypeId));
    });

    const allotmentsForDates = selectedAllotments.length > 0 ? selectedAllotments : this.hotelAllotments;

    const dateValues = allotmentsForDates.map(({ date }: { date: string }) => +new Date(date?.substring(0, 10)));

    if (dateValues.length === 0) {
      this.bookableDates = [];
      this.gapDates = {};
      this.checkinDate = null;
      this.checkoutDate = null;
      return;
    }
    const startDate = settings?.staticStartDate ? +new Date(settings?.staticStartDate) : Math.min(...dateValues);
    const endDate = settings.staticEndDate ? +new Date(settings?.staticEndDate - 86400000) : Math.max(...dateValues);

    this.bookableDates = [...new Set([...dateValues, +new Date(endDate + 86400000)])];

    this.gapDates = this.bookableDates.reduce((acc: any, date) => {
      const nextDate = +new Date(date + 86400000);

      if (!this.bookableDates.includes(nextDate) && date !== endDate + 86400000) {
        acc[date] = +new Date(nextDate);

        // Smaller dates than the current date
        const smallerDates = this.bookableDates
          .filter(x => x < date)
          .sort((a, b) => b - a)
          .map(x => new Date(x));

        for (let i = 0; i < smallerDates.length; i++) {
          const fillerDate = +new Date(date - (i + 1) * 86400000);

          if (fillerDate === +new Date(smallerDates[i]) && !acc[fillerDate]) {
            acc[fillerDate] = +new Date(nextDate);
          }
        }
      }
      return acc;
    }, {});

    this.checkinDate = startDate;
    this.checkoutDate = endDate + 86400000;
  }

  public async fetchRoomMates(eventId: number, orderId: number) {
    try {
      const response = await basedAxios.get(`./hotel/getroomates/${eventId}`);
      const orderData = (response?.data ?? [])?.filter((order: any) => order.orderId !== orderId);
      this.roomMates = orderData;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchHotels(eventId: number) {
    try {
      const response = await basedAxios.get(`./hotel/getHotelAllotments/${eventId}`);
      this.hotels = response?.data?.hotels ?? [];
      this.hotelAllotments = response?.data?.allotments ?? [];
      this.loaded = true;
      this.eventId = eventId;
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchBookableRoomSizes(eventId: number) {
    try {
      const response = await basedAxios.get(`./hotel/getBookableRoomSizes/${eventId}`);
      this.bookableRoomSizes = response.data?.sort((a: number, b: number) => a - b) ?? [];

      if (this.bookableRoomSizes.length > 0) {
        this.roomSizes = [this.bookableRoomSizes?.[0]];
        this.bookableRoomSizes = this.bookableRoomSizes;
      } else {
        this.roomSizes = [];
        this.bookableRoomSizes = [];
      }
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchAllotmentStatus(requestedAllotmentIds: number[]) {
    const sanitizedAllotmentIds = requestedAllotmentIds.filter(allotmentId => typeof allotmentId === 'number' && Number.isFinite(allotmentId));
    const sanitizedRoomSizes = this.roomSizes.filter(roomSize => typeof roomSize === 'number' && Number.isFinite(roomSize));
    const encodedAllotmentIds = encodeURIComponent(JSON.stringify(sanitizedAllotmentIds));
    const encodedRoomSizes = encodeURIComponent(JSON.stringify(sanitizedRoomSizes));
    const encodedStartDate = new Date(this.checkinDate ?? '')?.toISOString();
    const encodedEndDate = new Date(this.checkoutDate ?? '')?.toISOString();

    const url = `./hotel/CheckAllotmentStatus/${this.eventId}`;
    const searchParams = `?roomSizes=${encodedRoomSizes}&pricingIds=${encodedAllotmentIds}&start=${encodedStartDate}&end=${encodedEndDate}`;

    try {
      const response = await basedAxios.get(url + searchParams);

      if (!response.data.available) {
        this.fullbooked = true;
      } else {
        this.fullbooked = false;
      }
    } catch (error) {
      console.error(error);
    }
  }

  public async fetchAvailableAllotmentsBySelection(eventId: number) {
    const selectedRoomTypeIds = (this.selection ?? []).map(hotelRoomTypeId => Number(hotelRoomTypeId)).filter(hotelRoomTypeId => Number.isFinite(hotelRoomTypeId));

    if (selectedRoomTypeIds.length === 0) {
      this.fullbooked = false;
      return;
    }

    const searchParams = selectedRoomTypeIds.map(hotelRoomTypeId => `hotelRoomTypeId=${hotelRoomTypeId}`).join('&');
    const response = await basedAxios.get(`./hotel/CheckMultipleAllotmentStatus/${eventId}?${searchParams}`);
    this.fullbooked = !response.data.available;
  }

  public async search() {
    this.fetching = true;
    const sanitizedRoomSizes = this.roomSizes.filter(roomSize => typeof roomSize === 'number' && Number.isFinite(roomSize));
    const sanitizedSelection = (this.selection ?? []).map(selectionValue => Number(selectionValue)).filter(selectionValue => Number.isFinite(selectionValue));
    const encodedRoomSizes = encodeURIComponent(JSON.stringify(sanitizedRoomSizes));
    const encodedSelection = encodeURIComponent(JSON.stringify(sanitizedSelection));
    const encodedStartDate = new Date(this.checkinDate ?? '')?.toISOString();
    const encodedEndDate = new Date(this.checkoutDate ?? '')?.toISOString();

    const url = `./hotel/searchAvailableRooms/${this.eventId}`;
    const searchParams = `?roomSizes=${encodedRoomSizes}&count=${this.roomCount.toString()}&start=${encodedStartDate}&end=${encodedEndDate}&selection=${encodedSelection}`;

    try {
      const response = await basedAxios.get(url + searchParams);

      if (response.data.allotments.length === 0) {
        this.searchResultList = [];
        this.showNoResultMessage = true;
      } else {
        this.searchResultList = response.data.allotments;
      }

      this.fullbookedAllotments = [];
    } catch (error) {
      this.fetching = false;
    } finally {
      this.fetched = true;
      this.fetching = false;
    }
  }

  public get searchResults() {
    const groupedAllotments = this.searchResultList.reduce((groups, ha) => {
      const group = groups.find(({ roomTypeId }) => roomTypeId === ha.hotelRoomTypeId);
      if (group && !group.allotments.some(({ allotmentId }) => allotmentId === ha.allotmentId)) {
        group.allotments.push(ha);
      } else {
        groups.push({ roomTypeId: ha.hotelRoomTypeId, allotments: [ha] });
      }
      return groups;
    }, []);

    const allotmentList = groupedAllotments.flatMap(({ roomTypeId, allotments }) => {
      //Get hotel from room type
      const hotel = this.hotels.find(({ hotelRoomType }: any) => hotelRoomType.some(({ hotelRoomTypeId }) => hotelRoomTypeId === roomTypeId)) as any;

      if (!hotel) return [];

      // Room type name
      const roomType = (hotel as any)?.hotelRoomType.find(({ hotelRoomTypeId }) => hotelRoomTypeId === roomTypeId);

      // Get all sizes for the allotments (pricing options)
      const sizes: any[] = [...new Set(allotments.flatMap(({ hotelAllotmentPricing }) => hotelAllotmentPricing.map(({ roomSize }) => roomSize)))];

      return sizes.map(size => {
        const pricings = allotments.flatMap(({ hotelAllotmentPricing }) => hotelAllotmentPricing).filter(({ roomSize }) => roomSize === size);
        const pricingIds: number[] = pricings.map(({ allotmentPricingId }) => allotmentPricingId);
        const price = pricings.find(({ price }: { price: number }) => price)?.price ?? 0;
        const vatPercent = pricings.find(({ vatPercent }: { vatPercent: number }) => vatPercent)?.vatPercent ?? 0;
        const vatAmount = (price * (vatPercent / 100)).toFixed();

        const dateValues = allotments.map(({ date }) => +new Date(date.substring(0, 10)));

        const startDate = Math.min(...dateValues);
        const endDate = Math.max(...dateValues);

        const nights = allotments?.length ?? 0;

        const checkinDate = new Date(startDate).toISOString();
        const checkoutDate = new Date(new Date(endDate).setDate(new Date(endDate).getDate() + 1)).toISOString();

        if (!roomType) return null;
        return {
          hotelAllotment: allotments[0],
          roomTypeId: roomType.hotelRoomTypeId,
          imageUrl: roomType.imageUrl || hotel.imageUrl || '',
          name: hotel.name,
          type: roomType.name,
          size: roomType.roomSize,
          booked: allotments[0]?.booked,
          count: allotments[0]?.count,
          price,
          description: roomType.description,
          vatPercent, //
          vatAmount, //
          asRoomSize: size,
          allotmentPricingIds: pricingIds,
          nights,
          supplementPrice: this.rootStore?.breakouts?.find(({ eventId }: any) => eventId === allotments[0]?.supplementId)?.amount ?? 0,
          supplementVatPercent: this.rootStore?.breakouts?.find(({ eventId }: any) => eventId === allotments[0]?.supplementId)?.vatPercent ?? 0,
          dateRangeString: formatDateRange(`${checkinDate} - ${checkoutDate}`, this.rootStore?.environment?.lang ?? 'swe'),
        };
      });
    });

    return allotmentList;
  }
}
